import { useEffect, useRef, useState, type ComponentType } from 'react';
import { useTranslation } from '../../i18n';
import { useTheme, type ThemePreference } from '../../theme/ThemeProvider';
import { cn } from '../../lib/utils';
import { CheckIcon, MonitorIcon, MoonIcon, SunIcon } from '../icons';

const OPTIONS: ReadonlyArray<{
  value: ThemePreference;
  labelKey: 'theme.light' | 'theme.dark' | 'theme.system';
  Icon: ComponentType<{ className?: string }>;
}> = [
  { value: 'light', labelKey: 'theme.light', Icon: SunIcon },
  { value: 'dark', labelKey: 'theme.dark', Icon: MoonIcon },
  { value: 'system', labelKey: 'theme.system', Icon: MonitorIcon },
];

/**
 * Light / dark / system switcher.
 * Closes on outside click and on Escape, and restores focus to the trigger.
 */
export function ThemeToggle() {
  const { theme, resolvedTheme, setTheme } = useTheme();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent): void => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const TriggerIcon = resolvedTheme === 'dark' ? MoonIcon : SunIcon;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`${t('theme.label')}: ${t(`theme.${theme}` as const)}`}
        title={t('theme.label')}
        className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-muted transition-colors hover:bg-surface-muted hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      >
        <TriggerIcon className="h-5 w-5" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute end-0 z-50 mt-2 w-44 overflow-hidden rounded-xl border border-border bg-surface py-1 shadow-lg"
        >
          {OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              role="menuitemradio"
              aria-checked={theme === option.value}
              onClick={() => {
                setTheme(option.value);
                setOpen(false);
              }}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-2 text-start text-sm',
                'hover:bg-surface-muted focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-brand',
                theme === option.value ? 'text-fg' : 'text-muted',
              )}
            >
              <option.Icon className="h-4 w-4" />
              <span className="flex-1">{t(option.labelKey)}</span>
              {theme === option.value && <CheckIcon className="h-4 w-4 text-brand" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
